import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import GeneralContext from '../../utils/generalContext';
import mascotImg from '../../birmingham_mascot.png';


const VirtualTutorChat = () => {
    const { user, global } = useContext(GeneralContext);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: "¡Hola! Soy tu Tutor Virtual de Birmingham Institute. ¿En qué te ayudo hoy con tu inglés?" }
    ]);
    const [inputText, setInputText] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const recognitionRef = useRef(null);
    const messagesEndRef = useRef(null);
    const userid = user?.user?.user?.id_alumno;
    const location = useLocation();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, isOpen]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.lang = 'en-US'; 
            recognition.interimResults = true;
            recognition.continuous = true;

            recognition.onresult = (event) => {
                let currentTranscript = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    currentTranscript += event.results[i][0].transcript;
                }
                setInputText(currentTranscript);
            };

            recognition.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsListening(false);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            setInputText('');
            try {
                recognitionRef.current?.start();
                setIsListening(true);
            } catch (err) {
                console.error(err);
            }
        }
    };

    const formatMarkdown = (text) => {
        // Handle bold italic (PERFECT)
        let formatted = text.replace(/\*\*\*(.*?)\*\*\*/g, '<strong style="color: #6f42c1;"><em>$1</em></strong>');
        // Handle bold (PAST)
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #e83e8c;">$1</strong>');
        // Handle italic (FUTURE)
        formatted = formatted.replace(/\*(.*?)\*/g, '<em style="color: #007bff;">$1</em>');
        // Handle code block format `text` (PRESENT)
        formatted = formatted.replace(/`(.*?)`/g, '<code style="color: #28a745; background-color: #f8f9fa; padding: 0.1rem 0.3rem; border-radius: 4px;">$1</code>');
        // Handle new lines
        formatted = formatted.replace(/\n/g, '<br/>');
        return formatted;
    };

    const handleSend = async () => {
        if (!inputText.trim()) return;

        // Stop listening if sending
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        }

        const newMessages = [...messages, { role: 'user', content: inputText.trim() }];
        setMessages(newMessages);
        setInputText('');
        setIsTyping(true);

        try {
            const url = global.domain + "v3-api/v3/virtualtutor.json";
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userid: userid,
                    messages: newMessages
                })
            });

            if(response.status === 401) {
                const resJson = await response.json();
                setIsTyping(false);
                setMessages(prev => [...prev, { role: 'assistant', content: `**Error:** ${resJson.message}` }]);
                return;
            }

            const data = await response.json();

            if (data && data.choices && data.choices[0] && data.choices[0].message) {
                const assistantMsg = data.choices[0].message.content;
                setMessages(prev => [...prev, { role: 'assistant', content: assistantMsg }]);
                
                // Read text aloud if user was talking
                speakText(assistantMsg);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', content: "Lo siento, tuve un problema al procesar tu solicitud." }]);
            }
        } catch (error) {
            console.error(error);
            setMessages(prev => [...prev, { role: 'assistant', content: "Error de conexión con el Servidor." }]);
        } finally {
            setIsTyping(false);
        }
    };

    const speakText = (text) => {
        // Strip markdown before speaking
        let cleanText = text.replace(/[*_~`]/g, '');
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.lang = 'en-US';
        utterance.rate = 1.0;
        
        let voices = window.speechSynthesis.getVoices();
        let englishVoices = voices.filter(v => v.lang.startsWith('en'));
        let premium = englishVoices.find(v => v.name.includes("Google") || v.name.includes("Siri") || v.name.includes("Premium") || v.name.includes("Natural"));
        if(premium) utterance.voice = premium;

        window.speechSynthesis.speak(utterance);
    };

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
    }

    // Restrict chatbot to only English Course (ID = 1)
    const isEnglishCourse = location.pathname.startsWith('/resumen/1') || location.pathname.startsWith('/tracking/1') || location.pathname.startsWith('/consulta/1');

    if (!user || !isEnglishCourse) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '50%',
            transform: 'translateY(-50%)',
            right: '20px',
            zIndex: 9999,
            fontFamily: "'Inter', sans-serif"
        }}>
            {/* Widget Button */}
            {!isOpen && (
                <button 
                    onClick={() => setIsOpen(true)}
                    className="btn btn-success p-0 rounded-circle shadow border-white border-2"
                    style={{
                        width: '65px', 
                        height: '65px',
                        backgroundImage: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s',
                        overflow: 'hidden'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <img src={mascotImg} alt="Mascot" style={{width: '60px', height: '60px', objectFit: 'cover'}} className="rounded-circle" />
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div 
                    className="card shadow-lg border-0 d-flex flex-column"
                    style={{
                        width: '380px',
                        height: '550px',
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                    }}
                >
                    {/* Header */}
                    <div 
                        className="card-header text-white px-3 py-3 d-flex justify-content-between align-items-center"
                        style={{
                            backgroundImage: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            borderBottom: 'none'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <div className="bg-white rounded-circle d-flex align-items-center justify-content-center mr-2" style={{width: "40px", height: "40px", overflow: "hidden"}}>
                                <img src={mascotImg} alt="Tutor Mascot" style={{width: '40px', height: '40px', objectFit: 'cover'}} />
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold">Virtual Tutor</h6>
                                <small style={{opacity: 0.8}}>Birmingham Institute AI</small>
                            </div>
                        </div>
                        <div className="d-flex">
                            <button className="btn btn-sm text-white border-0" onClick={stopSpeaking} title="Stop Audio">
                                <i className="fas fa-volume-mute"></i>
                            </button>
                            <button className="btn btn-sm text-white border-0" onClick={() => setIsOpen(false)}>
                                <i className="fas fa-times fa-lg"></i>
                            </button>
                        </div>
                    </div>

                    {/* Chat Body */}
                    <div className="card-body bg-light overflow-auto p-3 d-flex flex-column" style={{flex: 1}}>
                        {messages.filter(m => m.role !== 'system').map((msg, index) => (
                            <div key={index} className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                                <div 
                                    className={`p-3 rounded shadow-sm`}
                                    style={{
                                        maxWidth: '85%',
                                        backgroundColor: msg.role === 'user' ? '#11998e' : '#ffffff',
                                        color: msg.role === 'user' ? '#ffffff' : '#333333',
                                        borderRadius: msg.role === 'user' ? '20px 20px 0px 20px' : '20px 20px 20px 0px',
                                        fontSize: '0.95rem'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
                                />
                            </div>
                        ))}
                        {isTyping && (
                            <div className="d-flex justify-content-start mb-3">
                                <div className="p-3 bg-white rounded shadow-sm text-muted" style={{borderRadius: '20px 20px 20px 0px'}}>
                                    <i className="fas fa-circle-notch fa-spin mr-2"></i> Procesando...
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="card-footer bg-white border-top-0 p-3">
                        <div className="input-group" style={{boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderRadius: '30px', overflow: 'hidden'}}>
                            <button 
                                className={`btn ${isListening ? 'btn-danger' : 'btn-light'} border-0`} 
                                onClick={toggleListening}
                                style={{padding: '10px 15px'}}
                                title="Dictate"
                            >
                                <i className={`fas ${isListening ? 'fa-stop-circle' : 'fa-microphone'} fa-lg`}></i>
                            </button>
                            
                            <input 
                                type="text" 
                                className="form-control border-0 bg-light" 
                                placeholder="Escribe o habla en inglés..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                style={{boxShadow: 'none', paddingLeft: '15px'}}
                            />
                            
                            <button className="btn border-0 text-white" 
                                onClick={handleSend}
                                disabled={!inputText.trim() || isTyping}
                                style={{backgroundImage: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', padding: '10px 20px'}}
                            >
                                <i className="fas fa-paper-plane"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VirtualTutorChat;
